'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ClipboardList, CircleCheck } from 'lucide-react';
import { useProject } from '@/features/project/hooks/use-projects';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import { SettingsSectionRow } from './SettingsSection';

interface ProjectGoalsSectionProps {
  projectId: string;
  /// When provided, the parent owns the sheet open state. Lets sibling rows
  /// (e.g. the Prompts banner link) jump straight into the goals editor
  /// instead of bouncing the user out to the standalone goals page.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Render-settings row that opens the shared `ProjectGoalsSheet` (goals +
 * region + facade area editor). Mirrors Apple's `Project goals`
 * SettingsDestination and Android's `ProjectGoalsPanelContent`. The editor
 * itself (buffer semantics, autosave) lives in `ProjectGoalsSheet`, shared
 * with the estimate editor's facade-area link.
 */
export function ProjectGoalsSection({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSectionProps) {
  const t = useTranslations('projectGoals');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isSheetOpen = isControlled ? open : internalOpen;
  const setIsSheetOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  const { data: projectResponse } = useProject(projectId);
  const hasGoals =
    (projectResponse?.data?.attributes.goals ?? '').trim().length > 0;

  return (
    <>
      <SettingsSectionRow
        icon={ClipboardList}
        title={t('label')}
        // Filled goals show a teal (primary) unfilled checkmark instead of a
        // "Set" text label — the open circle reads as a status badge, not a
        // tappable control. Empty state keeps the "Not set" text + orange dot.
        subtitle={hasGoals ? undefined : t('notSet')}
        subtitleContent={
          hasGoals ? (
            <CircleCheck
              className="h-5 w-5 text-primary"
              strokeWidth={1.5}
              aria-label={t('set')}
            />
          ) : undefined
        }
        needsAttention={!hasGoals}
        onClick={() => setIsSheetOpen(true)}
      />

      <ProjectGoalsSheet
        projectId={projectId}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
      />
    </>
  );
}
